import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Auth } from 'aws-amplify';
import { AIAssistantWidget } from '@amzn/wwasft-components';
import '@amzn/wwasft-components/styles.css';
import { getAIAssistantConfig } from 'src/config/aiAssistantConfig';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import {
  fetchFeatureFlags,
  isFeatureEnabled,
  FeatureFlag,
  FeatureFlagMap,
  defaultFlags,
} from 'src/config/featureFlags';

interface ChatWidgetContextValue {
  openChat: () => void;
  isEnabled: boolean;
}

const ChatWidgetContext = createContext<ChatWidgetContextValue>({
  openChat: () => {},
  isEnabled: false,
});

export const useChatWidget = () => useContext(ChatWidgetContext);

export const ChatWidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlagMap>(defaultFlags);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const { alias } = useUserAlias();

  useEffect(() => {
    fetchFeatureFlags().then(setFlags);
  }, []);

  const isEnabled = isFeatureEnabled(flags, FeatureFlag.AI_ASSISTANT);

  const getAuthToken = useCallback(async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }, []);

  const handleClose = useCallback(() => setIsChatOpen(false), []);
  const openChat = useCallback(() => setIsChatOpen(true), []);

  const config = useMemo(
    () => ({
      ...getAIAssistantConfig(),
      getAuthToken,
      userAlias: alias,
      visibleToolbarActions: ['new-chat' as const],
    }),
    [getAuthToken, alias]
  );

  const contextValue = useMemo(() => ({ openChat, isEnabled }), [openChat, isEnabled]);

  return (
    <ChatWidgetContext.Provider value={contextValue}>
      {children}
      {isEnabled &&
        createPortal(
          <AIAssistantWidget config={config} isOpen={isChatOpen} onClose={handleClose} />,
          document.body
        )}
    </ChatWidgetContext.Provider>
  );
};
